import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import AddEntitiesDialog from '../common/sectionsControl/uiControls/projectsControl/AddEntitiesDialog';
import { UserState } from '../../store/User';
import { chargeRateField } from './usage/ResourceUsageGrid';
import * as ResourcesListStore from '../../store/ResourcesListStore';
import PersonPickerInput, { PersonInfo } from '../common/inputs/PersonPickerInput';
import { Dropdown, IDropdownOption, MessageBar, MessageBarType, Toggle } from 'office-ui-fabric-react';
import { formatFieldValue } from '../utils/common';
import * as Metadata from '../../entities/Metadata';
import { EntityType, EstimatedChargeCalculationMode } from '../../entities/common';
import * as CostRateTableStore from '../../store/CostRateTableStore';
import { bindActionCreators } from 'redux';

type OwnProps = {
    entityid: string;
    entityType: EntityType | string;
    resource: ResourcesListStore.Resource;
    isForResourcePlan?: boolean;
    /// entity resourceIds
    resourceIds: string[];
    onReplaceResource: (newResourceIds: string[], keepRateOnReplace: boolean, sumUpPlanHours: boolean, sumUpActualHours: boolean, costRateTableId?: string) => void;
    onDismiss: () => void;
}
type StoreProps = {
    user: UserState;
    costRateTables: CostRateTableStore.CostRateTableState;
    resources: ResourcesListStore.ResourcesState;
}

type ActionProps = {
    resourceActions: typeof ResourcesListStore.actionCreators;
}

type Props = OwnProps & StoreProps & ActionProps;

const ReplaceResourceDialog = (props: Props) => {

    const [keepRateOnReplace, setKeepRateOnReplace] = React.useState(true);
    const [sumUpPlanHours, setSumUpPlanHours] = React.useState(true);
    const [sumUpActualHours, setSumUpActualHours] = React.useState(false);
    const [isAlreadyAdded, setIsAlreadyAdded] = React.useState(false);
    const [selectedResourceId, setSelectedResourceId] = React.useState<string | undefined>(undefined);
    const [selectedCostRateTableId, setSelectedCostRateTableId] = React.useState<string | undefined>(undefined);

    const estimatedChargeCalculationMode = props.resource.usage?.byEntity?.[props.entityid]?.attributes?.estimatedChargeCalculationMode;
    const isUseCostRateTablesMode = estimatedChargeCalculationMode === EstimatedChargeCalculationMode.UseCostRateTables;

    const _hasCostsAndRatesForCostRateTable = (resourceId: string, costRateTableId: string) => {
        const resource = props.resources.byId[resourceId];
        if (!resource) {
            return false;
        }

        const hasRates = !!resource.timephasedRates?.some(_ =>
            _.attributes?.CostRateTable?.id === costRateTableId
            && _.attributes?.StandardRate != null);

        const hasCosts = !!resource.timephasedCosts?.some(_ =>
            _.attributes?.CostRateTable?.id === costRateTableId
            && _.attributes?.StandardCost != null);

        return hasRates && hasCosts;
    }

    const availableCostRateTables = React.useMemo<CostRateTableStore.CostRateTable[]>(() => {
        if (!selectedResourceId) {
            return [];
        }

        return props.costRateTables.allIds
            .map(id => props.costRateTables.byId[id])
            .filter((_) => !!_)
            .filter(table => _hasCostsAndRatesForCostRateTable(selectedResourceId, table.id));
    }, [props.costRateTables.allIds, props.costRateTables, props.resources, selectedResourceId]);

    React.useEffect(() => {
        if (!isUseCostRateTablesMode) {
            setSelectedCostRateTableId(undefined);
            return;
        }

        if (availableCostRateTables.length === 1) {
            setSelectedCostRateTableId(availableCostRateTables[0].id);
            return;
        }

        if (selectedCostRateTableId && availableCostRateTables.some(_ => _.id === selectedCostRateTableId)) {
            return;
        }

        const defaultTable = availableCostRateTables.find(_ => _.isDefault);
        if (defaultTable) {
            setSelectedCostRateTableId(defaultTable.id);
            return;
        }

        if (selectedCostRateTableId) {
            setSelectedCostRateTableId(undefined);
        }
    }, [availableCostRateTables, isUseCostRateTablesMode, selectedCostRateTableId]);

    const _renderEntitiesSelector = (onChanged: (selected?: string[]) => void) => {
        const { user, resource, resourceIds, entityType, entityid, isForResourcePlan } = props;
        const bookingRateValue = chargeRateField.calculate([resource], [entityid]);

        return <>
            <PersonPickerInput
                multichoice={false}
                onChanged={(value) => {
                    setIsAlreadyAdded(value && resourceIds.indexOf((value as PersonInfo).id) > -1);
                    if (value) {
                        const id = (value as PersonInfo).id;
                        setSelectedResourceId(id);
                        props.resourceActions.loadResource(id, true);
                        onChanged([id]);
                    } else {
                        setSelectedResourceId(undefined);
                        onChanged(undefined);
                    }
                }}
                searchUrl={`api/resource/findAllowed${isForResourcePlan ? "?resourcePlan=true" : ""}`}
                filter={{ exceptIds: [resource.id] }}
                noResultsFoundText={PersonPickerInput.NoResourceFound}
                inputProps={{ placeholder: "Type to search", autoFocus: true }}
            />
            {
                isAlreadyAdded &&
                <MessageBar messageBarType={MessageBarType.warning}>
                    Selected resource is already added to the {entityType}.
                </MessageBar>
            }
            {
                isUseCostRateTablesMode
                    ? <Dropdown
                        label="Cost Rate Table"
                        disabled={!selectedResourceId || availableCostRateTables.length === 0}
                        selectedKey={selectedCostRateTableId}
                        onChange={(e, option) => setSelectedCostRateTableId(option?.key as string)}
                        options={availableCostRateTables.map((_) => ({ key: _.id, text: _.name } as IDropdownOption))}
                        placeholder={selectedResourceId ? 'Select cost rate table' : 'Select resource first'}
                    />
                    : <Toggle label={`Keep Charge Rate ${formatFieldValue(bookingRateValue, Metadata.FormatType.Cost, user)}`}
                        checked={keepRateOnReplace}
                        onChange={(e, checked) => setKeepRateOnReplace(!!checked)}
                        onText='Yes'
                        offText='No' />
            }
            <Toggle label={`${isAlreadyAdded ? 'Sum up' : 'Keep'} Planned Hours`}
                checked={sumUpPlanHours}
                onChange={(e, checked) => setSumUpPlanHours(!!checked)}
                onText='Yes'
                offText='No' />
            {entityType !== EntityType.Idea && <Toggle label={`${isAlreadyAdded ? 'Sum up' : 'Keep'} Actual Hours`}
                checked={sumUpActualHours}
                onChange={(e, checked) => setSumUpActualHours(!!checked)}
                onText='Yes'
                offText='No' />}
        </>;
    }

    const _onComplete = (newResourceIds: string[]) => {
        props.onReplaceResource(
            newResourceIds,
            isUseCostRateTablesMode ? false : keepRateOnReplace,
            sumUpPlanHours,
            sumUpActualHours,
            isUseCostRateTablesMode ? selectedCostRateTableId : undefined);
        props.onDismiss();
    }

    return <AddEntitiesDialog
        confirmButtonText="Replace"
        isConfirmDisabled={isUseCostRateTablesMode && (!selectedCostRateTableId || availableCostRateTables.length === 0)}
        dialogContentProps={{
            title: 'Replace Resource',
            subText: 'Select resource for the replacement'
        }}
        onDismiss={props.onDismiss}
        onComplete={(newResourceIds) => _onComplete(newResourceIds)}
        renderEntitySelector={_renderEntitiesSelector}
    />;
}
function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StoreProps {
    return {
        user: state.user,
        costRateTables: state.costRateTables,
        resources: state.resources,
    };
}

function mapDispatchToProps(dispatch: any): ActionProps {
    return {
        resourceActions: bindActionCreators(ResourcesListStore.actionCreators, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ReplaceResourceDialog);
